// @flow
import type { Node } from 'react';
import React, { useState, useContext, useMemo } from 'react';
import httpService, { storedAccessToken } from '../../services/http';
import settings from '../../settings';
import { useStructureContext } from '../StructureProvider';
// import { plotResults } from './dummyContextData';

type Props = {
  children: Node,
};

type ResponseType = {
  id: number,
  plot_data: any,
  parameters: any,
  stop_time: number,
  output_interval: number,
};

export type contextType = {
  isLoading: boolean,
  setIsLoading: any,
  isSimulating: boolean,
  setIsSimulating: any,
  resultData: any,
  plotData: any,
  uiParameter: any,
  setQuery: any,
  timeIndex: number,
  setTimeIndex: any,
  stopTime: Array<number>,
  outputInterval: Array<number>,
  invalidateCachedResponse: (id: number) => void,
};

export const PlotDataContext: React$Context<contextType> = React.createContext<contextType>({
  isLoading: true,
  setIsLoading: () => {},
  isSimulating: false,
  setIsSimulating: () => {},
  resultData: [],
  plotData: [],
  uiParameter: null,
  setQuery: () => {},
  timeIndex: 0,
  setTimeIndex: () => {},
  stopTime: [1],
  outputInterval: [1],
  invalidateCachedResponse: () => {},
});

const PlotDataProvider = ({ children }: Props): Node => {
  const [resultData, setResultData] = useState([]);
  const [plotData, setPlotData] = useState([]);
  const [uiParameter, setUiParameter] = useState();
  const [cachedResponses, setCachedResponses] = useState({});
  const [query, setQuery] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSimulating, setIsSimulating] = useState(false);
  const [timeIndex, setTimeIndex] = useState(0);
  const [stopTime, setStopTime] = useState([1]);
  const [outputInterval, setOutputInterval] = useState([1]);

  const structureContext = useStructureContext();

  /**
   * @description handles the plotdata and uiParameter for each app
   * they are merged with up-to-date data.
   * isLoading is set false at the end.
   * @param responseData Array of plotdata from API response
   * @returns {*}
   */
  const handleResultData = (responseData: Array<ResponseType>) => {
    const results = responseData;

    // set stop time and output interval so that the time index can be calculated in timeseries plot component
    // stop time and output interval are set for all results in case a differentiation is necessary in the future
    // in ResultsPlotComponent the first entry is used
    setStopTime(results.map((res) => (res.stop_time ? res.stop_time : 1)));
    setOutputInterval(results.map((res) => (res.output_interval ? res.output_interval * 1000 : 1)));

    // make sure that old simulation results work with up-to-date PlotData structure
    const resultPlotData = responseData.map((r) => {
      if (typeof r.plot_data === 'string') {
        return r.plot_data;
      }
      return r.plot_data;
    });

    let completePlotData = [];
    completePlotData = resultPlotData;
    setPlotData(completePlotData);
    // setPlotData([plotResults]);

    // make sure that old simulation results work with up-to-date UIParameter structure
    const resultUiParameter = responseData.map((r) => {
      if (typeof r.parameters === 'string') {
        return r.parameters;
      }
      return r.parameters;
    });
    let completeUiParameter = [];

    completeUiParameter = resultUiParameter.map((r) => ({
      ...structureContext.initialValues,
      ...r,
    }));
    setUiParameter(completeUiParameter);

    for (let i = 0; i < results.length; i += 1) {
      results[i].parameters = completeUiParameter[i];
      results[i].plot_data = completePlotData[i]; // viSimResults;//plotResults;//
    }

    setResultData(results);
    setIsLoading(false);
  };

  /**
   * @description get request for plotdata
   * @param queries Array of queries
   * @returns {Promise}
   */
  const getPlotData = (queries: Array<number>) => {
    const promiseResultData = httpService.get(`simulation/${settings.product}/plotdata/`, {
      headers: null,
      id: JSON.stringify(queries),
    });
    return Promise.resolve(promiseResultData);
  };

  /**
   * @description executed on change of query
   * handles cached data as well as the call for new data.
   * The data are stored as raw request data and handleResultData()
   * is called every time.
   */
  useMemo(() => {
    if (storedAccessToken()) {
      // retrieves the data already cached and stores them in requestedAndCachedData
      const cachedIds = Object.keys(cachedResponses);
      const nonCachedIds = query.filter((q) => cachedIds.includes(JSON.stringify(q)) === false);
      const cachedAndRequestedIds = query.filter((q) => cachedIds.includes(JSON.stringify(q)));

      const cachedResponseValues = (Object.values(cachedResponses): any);
      const requestedAndCachedData = cachedResponseValues.filter((c) => cachedAndRequestedIds.includes(c.id));

      // if there are uncached data, call getPlotData() and cache the response
      // call handleResultData() in each case
      if (nonCachedIds.length > 0) {
        getPlotData(nonCachedIds)
          .then((response) => {
            const tmpCachedResponses = cachedResponses;
            // response.data.forEach(r => (tmpCachedResponses[r.id] = r));
            for (let i = 0; i < response.data.length; i += 1) {
              const data = response.data[i];
              tmpCachedResponses[data.id] = data;
            }

            setCachedResponses(tmpCachedResponses);
            handleResultData([...response.data, ...requestedAndCachedData]);
            return true;
          })
          // give message to user --> modal in Dashboard
          .catch((error) => error);
      } else {
        handleResultData(requestedAndCachedData);
      }
    }
  }, [query]);

  const invalidateCachedResponse = (id: number): void => {
    const newCachedResponses = { ...cachedResponses };
    delete newCachedResponses[id];
    setCachedResponses(newCachedResponses);
  };

  return (
    <PlotDataContext.Provider
      value={{
        isLoading,
        setIsLoading,
        isSimulating,
        setIsSimulating,
        resultData,
        plotData,
        uiParameter,
        setQuery,
        timeIndex,
        setTimeIndex,
        stopTime,
        outputInterval,
        invalidateCachedResponse,
      }}
    >
      {children}
    </PlotDataContext.Provider>
  );
};

export const usePlotDataContext = (): contextType => {
  const context = useContext(PlotDataContext);
  if (context === undefined) {
    throw new Error('usePlotDataContext must be used within a PlotDataProvider');
  }
  return context;
};

export default PlotDataProvider;
